import { useEffect, useState, useCallback } from "react";
import { getNetworkNodeSets, getProfilerComparator } from "../api/quontxApi";
import SidePanel from "../components/SidePanel";
import RadarPlotFilters from "../components/RadarPlotFilters";
import ScatterPlotFilters from "../components/ScatterPlotFilters";
import DumbbellPlotFilters from "../components/DumbbellPlotFilters";
import ProfileComparator1_1Filters from "../components/Comparator1_1Filters";
import ProfileComparator1_NFilters from "../components/Comparator1_NFilters";
import LinePlotFilters from "../components/LinePlotFilters";
import ProfileFilter from "../components/ProfileFilters";
import { getPaginatedData } from "../utils";

const OrgProfileAnalysisPage = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [activeChart, setActiveChart] = useState("radar");
  const [displayData, setDisplayData] = useState();
  const [isLoading, setIsLoading] = useState(false);

  // NOTE: We intentionally do NOT preload entities/proxies
  const [domains, setDomains] = useState([]);

  // Memoize the fetch functions to prevent infinite loops
  const profileComparatorFetch = useCallback(async (params) => {
    return await getProfilerComparator({
      nodeA: params.nodeA,
      nodeB: params.nodeB
    });
  }, []);

  const profileComparator1NFetch = useCallback(async (params) => {
    return await getProfilerComparator({
      nodeA: params.nodeA,
      // nodeB is not passed (empty)
    });
  }, []);

  // Filter out Risk names or client side ?
  const loadNodeSets = async () => {
    const nodeSets = await getPaginatedData(getNetworkNodeSets, { itemsPerPage: 100 });

    setDomains(
      (nodeSets.data ?? []).slice().sort((a, b) => {
        if (a.description > b.description) return 1;
        if (a.description < b.description) return -1;
        return 0;
      })
    );
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        await loadNodeSets();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex relative">
      <SidePanel
        collapseCallback={() => setCollapsed(!collapsed)}
        collapsed={collapsed}
        data={displayData}
      />

      <div className="grow">
        <div className="p-6 pb-0">
          <h1 className="mb-6 text-center">Org Profile Analysis</h1>

          <div className="w-full flex flex-col items-center content-center overflow-y-auto custom-scrollbar h-[83vh]">
            {domains && (
              <div className="flex flex-col items-center content-center gap-6 w-full">
                <div className="flex gap-4 items-center content-center">
                  <button
                    onClick={() => setActiveChart("radar")}
                    className={`${
                      activeChart === "radar" ? "bg-blue-800" : "bg-blue-500"
                    }  text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Org Profile Radar
                  </button>

                  <button
                    onClick={() => setActiveChart("scatter")}
                    className={`${
                      activeChart === "scatter" ? "bg-blue-800" : "bg-blue-500"
                    }  text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Org Profile Scatter
                  </button>

                  <button
                    onClick={() => setActiveChart("org_similiaraty1_1")}
                    className={`${
                      activeChart === "org_similiaraty1_1" ? "bg-blue-800" : "bg-blue-500"
                    }  text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Org Profile Similarity 1-1
                  </button>

                  <button
                    onClick={() => setActiveChart("org_similiaraty1_n")}
                    className={`${
                      activeChart === "org_similiaraty1_n" ? "bg-blue-800" : "bg-blue-500"
                    }  text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Org Profile Similarity 1-N
                  </button>

                  {/* <button
                    onClick={() => setActiveChart("dumbbell")}
                    className={`${
                      activeChart === "dumbbell" ? "bg-blue-800" : "bg-blue-500"
                    } disabled:opacity-[0.4] text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Dumbbell
                  </button> */}

                  {/* <button
                    onClick={() => setActiveChart("line")}
                    className={`${
                      activeChart === "line" ? "bg-blue-800" : "bg-blue-500"
                    } disabled:opacity-[0.4] text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Line
                  </button> */}

                  <button
                    onClick={() => setActiveChart("profile")}
                    className={`${
                      activeChart === "profile" ? "bg-blue-800" : "bg-blue-500"
                    } disabled:opacity-[0.4] text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`}
                  >
                    Org Profile Stats
                  </button>
                </div>

                <div className="w-full">
                  {activeChart === "radar" && (
                    <RadarPlotFilters
                      propagnateData={(value) => setDisplayData(value)}
                      domains={domains}
                      isLoading={isLoading}
                    />
                  )}

                  {activeChart === "scatter" && (
                    <ScatterPlotFilters
                      propagnateData={(value) => setDisplayData(value)}
                      domains={domains}
                      isLoading={isLoading}
                    />
                  )}

                  {activeChart === "org_similiaraty1_1" && (
                    <ProfileComparator1_1Filters
                      propagnateData={(value) => setDisplayData(value)}
                      isLoading={isLoading}
                      fetchDataFunction={profileComparatorFetch}
                    />
                  )}

                  {activeChart === "org_similiaraty1_n" && (
                    <ProfileComparator1_NFilters
                      propagnateData={(value) => setDisplayData(value)}
                      isLoading={isLoading}
                      fetchDataFunction={profileComparator1NFetch}
                    />
                  )}

                  {activeChart === "dumbbell" && (
                    <DumbbellPlotFilters
                      propagnateData={(value) => setDisplayData(value)}
                      domains={domains}
                      isLoading={isLoading}
                    />
                  )}

                  {activeChart === "line" && (
                    <LinePlotFilters
                      propagnateData={(value) => setDisplayData(value)}
                      domains={domains}
                      isLoading={isLoading}
                    />
                  )}

                  {activeChart === "profile" && (
                    <ProfileFilter
                      propagnateData={(value) => setDisplayData(value)}
                      isLoading={isLoading}
                    />
                  )}
                </div>
              </div>
            )}

            {!domains?.length && !isLoading && (
              <p className="text-sm opacity-70">No domains loaded.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgProfileAnalysisPage;